import { ERROR_MESSAGES } from '@/shared/constants';
import {
  API_ERROR_MESSAGES,
  API_ERROR_MESSAGE_BY_TEXT,
  API_ERROR_MESSAGE_PATTERNS,
  API_HTTP_STATUS_MESSAGES,
} from '@/shared/constants/apiResponseCodes';

const IGNORED_DETAIL_KEYS = new Set(['code', 'status', 'message']);

/**
 * @param {unknown} error
 * @returns {string}
 */
export function getApiErrorCode(error) {
  return String(error?.response?.data?.code || error?.code || '').trim();
}

/**
 * @param {string} rawMessage
 * @returns {string|null}
 */
function resolveMessageByText(rawMessage) {
  const trimmed = String(rawMessage || '').trim();
  if (!trimmed) return null;

  if (API_ERROR_MESSAGE_BY_TEXT[trimmed]) {
    return API_ERROR_MESSAGE_BY_TEXT[trimmed];
  }

  for (const { pattern, message } of API_ERROR_MESSAGE_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match) {
      return typeof message === 'function' ? message(match, trimmed) : message;
    }
  }

  return null;
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function getRawBackendMessage(error) {
  return String(error?.response?.data?.message || error?.message || '').trim();
}

/**
 * Vraća korisničku poruku za API grešku, sa prioritetom centralizovanog mapiranja.
 * @param {unknown} error
 * @param {string} [fallback=ERROR_MESSAGES.GENERIC_ERROR]
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback = ERROR_MESSAGES.GENERIC_ERROR) {
  const code = getApiErrorCode(error);
  const rawMessage = getRawBackendMessage(error);

  if (code && API_ERROR_MESSAGES[code]) {
    return API_ERROR_MESSAGES[code];
  }

  const byText = resolveMessageByText(rawMessage);
  if (byText) return byText;

  const details = error?.response?.data?.details;
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    const fieldMessages = Object.entries(details)
      .filter(([key]) => !IGNORED_DETAIL_KEYS.has(key))
      .map(([field, message]) => {
        const translated = resolveMessageByText(String(message)) || String(message);
        return `${field}: ${translated}`;
      })
      .join('; ');
    if (fieldMessages) return fieldMessages;
  }

  const status = error?.response?.status;
  if (status && API_HTTP_STATUS_MESSAGES[status] && rawMessage) {
    const rawLooksEnglish = /[a-zA-Z]/.test(rawMessage);
    if (rawLooksEnglish) return API_HTTP_STATUS_MESSAGES[status];
  }

  if (status && API_HTTP_STATUS_MESSAGES[status] && !rawMessage) {
    return API_HTTP_STATUS_MESSAGES[status];
  }

  return rawMessage || fallback;
}

/**
 * @param {unknown} error
 * @param {string} fallbackMessage
 * @returns {Error}
 */
export function toApiError(error, fallbackMessage) {
  const message = getApiErrorMessage(error, fallbackMessage);
  const next = new Error(message);
  next.response = error?.response;
  next.cause = error;
  return next;
}
